import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import screens from '../datos/journey1.json';
import web from '../datos/journey1-web.json';
import { session, emptyLead, j1LogRow, saveSession } from '../engine/store';
import { SINONIMOS, norm, tileByMomentoCode } from '../engine/momentos';
import { Header } from '../components/Header';
import { ProtectionRing } from '../components/ProtectionRing';
import { StoryRing } from '../components/StoryRing';
import { tileIcon, IconArrowRight, IconAdvisor, IconCheck, IconSearch } from '../components/icons';
import { getMarcaActiva } from '../engine/marcas';

// Marca blanca: reemplaza {marca} y el literal "MBM" por la entidad activa.
const mt = (s) => {
  const m = getMarcaActiva();
  return String(s == null ? '' : s).replace(/\{marca\}/g, m.nombre).replace(/\bMBM\b/g, m.nombre);
};

export default function Wizard({ auto = false, onHome }) {
  // Prefill desde el buscador de la landing: el momento ya viene elegido → arranca en el paso 2
  const prefillRef = useRef(null);
  if (prefillRef.current === null) {
    const pm = session.prefillMomento;
    prefillRef.current = pm && pm.momento ? { ...emptyLead(), ...pm } : null;
    session.prefillMomento = false;
  }
  const prefilled = prefillRef.current;
  const [idx, setIdx] = useState(prefilled ? 1 : 0);
  const [lead, setLead] = useState(() => {
    if (prefilled) {
      session.lead = prefilled;
      return prefilled;
    }
    return emptyLead();
  });
  const [multiSel, setMultiSel] = useState([]);
  const [form, setForm] = useState({ nombre: '', tel: '', consent: false });
  const [formErr, setFormErr] = useState({});
  const [closing, setClosing] = useState(null); // 'asesor' → pantalla de cierre
  const autoRef = useRef(auto);

  const screen = screens[idx];
  const isMap = Boolean(screen.map);
  const isForm = Boolean(screen.form);
  const isMulti = Boolean(screen.multi) || screen.key === 'quienes';

  const dCode = lead.momento ? String(lead.momento).slice(0, 2) : null;

  const writeLog = useCallback((i) => {
    if (session.j1log[i]) return;
    session.j1log[i] = j1LogRow(screens[i], i);
    saveSession({ journey: 1, paso: i, lead: session.lead, log: session.j1log.filter(Boolean) });
  }, []);

  // Con prefill el paso "momento" no se visita: se escribe su renglón del log igual
  useEffect(() => {
    if (prefilled) writeLog(0);
  }, [prefilled, writeLog]);

  const persist = useCallback((next) => {
    setLead((l) => {
      const merged = { ...l, ...next };
      session.lead = merged;
      return merged;
    });
  }, []);

  // Orden de fichas de "cuidar": relevantes al momento primero (sugerido)
  const cuidarTiles = useMemo(() => {
    if (!isMulti) return screen.tiles;
    const rel = (web.wizard.relevancia_por_momento[dCode] || []);
    const suggested = screen.tiles
      .filter((t) => rel.includes(t.v))
      .map((t) => ({ ...t, sugerido: true }));
    const rest = screen.tiles.filter((t) => !rel.includes(t.v));
    return [...suggested, ...rest];
  }, [isMulti, screen, dCode]);

  const goNext = useCallback(() => {
    writeLog(idx);
    setIdx((i) => Math.min(i + 1, screens.length - 1));
  }, [idx, writeLog]);

  const chooseSingle = useCallback(
    (tile) => {
      if (screen.key === 'momento') persist({ momento: tile.v, go: tile.go || null, caso: ['A', 'B', 'C'].includes(tile.go) ? tile.go : null });
      else if (screen.key === 'quienes') persist({ quienes: tile.v });
      else if (screen.key === 'yapaga') persist({ yapaga: tile.v });
      goNext();
    },
    [screen, persist, goNext],
  );

  const toggleMulti = useCallback((v) => {
    // Valores excluyentes: "Solo yo" (quienes) y "No sé" (yapaga).
    const EXCL = ['Sin dependientes', 'Por auditar con el asesor'];
    setMultiSel((sel) => {
      if (sel.includes(v)) return sel.filter((x) => x !== v);
      if (EXCL.includes(v)) return [v];
      return [...sel.filter((x) => !EXCL.includes(x)), v];
    });
  }, []);

  const continueMulti = useCallback(() => {
    if (screen.key === 'quienes') persist({ quienes: multiSel.join(', ') });
    else if (screen.key === 'yapaga') persist({ yapaga: multiSel.join(', ') });
    else persist({ cuidar: multiSel });
    goNext();
  }, [multiSel, persist, goNext, screen]);

  const submitForm = useCallback(() => {
    const v = validarForm(form);
    if (!v.ok) {
      setFormErr({ nombre: !v.nombre, tel: !v.tel, consent: !form.consent });
      return;
    }
    setFormErr({});
    persist({
      nombre: limpiarNombre(form.nombre),
      tel: form.tel.trim(),
      consent: { si: true, ts: new Date().toISOString(), texto: web.formulario.consentimiento },
    });
    goNext();
  }, [form, persist, goNext]);

  // selección multi limpia al cambiar de pantalla
  useEffect(() => {
    const rel = web.wizard.relevancia_por_momento[dCode] || [];
    setMultiSel(screens[idx].key === 'cuidar' && rel.length ? [rel[0]] : []);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const back = useCallback(() => {
    if (idx === 0) return onHome();
    setIdx((i) => Math.max(i - 1, 0));
    return undefined;
  }, [idx, onHome]);

  // Salidas del mapa
  const seguirChat = useCallback(() => {
    writeLog(idx);
    session.lead = lead;
    session.fromJ1 = true;
    session.autoContinue = autoRef.current;
    const go = ['A', 'B', 'C'].includes(lead.go) ? lead.go : 'A';
    window.location.hash = autoRef.current ? `#${go}-auto` : `#${go}`;
  }, [idx, lead, writeLog]);

  const escribirAsesor = useCallback(() => {
    writeLog(idx);
    session.lead = lead;
    session.fromJ1 = true;
    session.j1log.push({
      turno: session.j1log.length + 1, journey: 1, strike: 0,
      agente: 'Orquestador de desborde', regla: 'Salida del mapa · que me escriba un asesor',
      fila: '—', dec: 'escala', txt: 'Lead entregado al asesor con el mapa', ts: new Date().toISOString(),
    });
    saveSession({ journey: 1, paso: idx, ended: true, lead: session.lead, log: session.j1log.filter(Boolean) });
    setClosing('asesor');
  }, [idx, lead, writeLog]);

  // ▶ Auto (#J1-auto): recorre el wizard solo (pausa visible ≥ 900 ms por pantalla) y sigue al caso A
  useEffect(() => {
    if (!autoRef.current || closing) return undefined;
    const delay = isMap ? 2200 : 1300; // el mapa se deja leer más (anillo se arma < 600 ms)
    const t = setTimeout(() => {
      if (isMap) {
        if (['A', 'B', 'C'].includes(lead.go)) seguirChat();
        else escribirAsesor();
        return;
      }
      if (isForm) {
        setForm({ nombre: 'Demo', tel: '300 000 0000', consent: true });
        setTimeout(() => {
          persist({ nombre: 'Demo', tel: '300 000 0000', consent: { si: true, ts: new Date().toISOString(), texto: web.formulario.consentimiento } });
          goNext();
        }, 700);
        return;
      }
      if (isMulti) {
        const first = cuidarTiles[0];
        setMultiSel([first.v]);
        setTimeout(() => {
          const key = screen.key === 'quienes' ? 'quienes' : screen.key === 'yapaga' ? 'yapaga' : 'cuidar';
          persist({ [key]: key === 'cuidar' ? [first.v] : first.v });
          goNext();
        }, 700);
        return;
      }
      chooseSingle(screen.tiles[0]);
    }, delay);
    return () => clearTimeout(t);
  }, [idx, isMap, isForm, isMulti, closing, screen, cuidarTiles, lead.go, chooseSingle, persist, goNext, seguirChat, escribirAsesor]);

  if (closing === 'asesor') {
    const c = web.cierre;
    const nombre = lead.nombre || 'listo';
    return (
      <div className="wiz" data-testid="wizard">
        <Header title="Mapa de protección" onHome={onHome} />
        <div className="wiz-body">
          <section className="wiz-cierre" data-testid="wizard-cierre">
            <ProtectionRing filled={Math.min((lead.cuidar?.length || 0) + 2, 8)} size={150}>
              <IconAdvisor size={30} color="#2F6B4F" />
            </ProtectionRing>
            <h1>{mt(c.titulo.replace('{nombre}', nombre))}</h1>
            <p className="wiz-cierre-sub">{mt(c.sub)}</p>
            <div className="wiz-recibe">
              <span className="wiz-recibe-k">Lo que recibe el asesor</span>
              <p>{mt(c.recibe)}</p>
            </div>
            <button type="button" className="btn-ghost" onClick={onHome} data-testid="wizard-cierre-home">
              Volver al inicio
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="wiz" data-testid="wizard">
      <Header title="Cuéntanos qué está cambiando" onHome={onHome} />

      <div className="wiz-progress" data-testid="wizard-progress">
        <button type="button" className="wiz-back" onClick={back} data-testid="wizard-back">
          {web.wizard.atras}
        </button>
        <div className="wiz-steps">
          {screens.map((_, i) => (
            <span key={i} className={`wiz-step ${i <= idx ? 'on' : ''}`} />
          ))}
        </div>
        <span className="wiz-count">{web.wizard.progreso.replace('n', idx + 1)}</span>
      </div>

      <div className="wiz-body" key={idx}>
        <div className="wiz-inner">
          <h1 className="wiz-q" data-testid="wizard-q">{screen.q}</h1>

          {/* Mapa de protección */}
          {isMap ? (
            <MapView lead={lead} onChat={seguirChat} onAsesor={escribirAsesor} />
          ) : isForm ? (
            <FormView web={web} form={form} setForm={setForm} err={formErr} onSubmit={submitForm} />
          ) : screen.key === 'momento' ? (
            <MomentoPicker screen={screen} onPick={chooseSingle} />
          ) : (
            <>
              <p className="wiz-hint">
                {screen.key === 'quienes' && isMulti
                  ? 'Marca todos los que apliquen.'
                  : isMulti ? web.wizard.hint_multi : web.wizard.hint_single}
              </p>
              <div className={`wiz-tiles ${isMulti ? 'multi' : ''}`} data-testid="wizard-tiles">
                {(isMulti ? cuidarTiles : screen.tiles).map((tile, i) => {
                  const Icon = tileIcon(screen.key, tile.v);
                  const sel = isMulti && multiSel.includes(tile.v);
                  const ord = sel ? multiSel.indexOf(tile.v) + 1 : 0;
                  return (
                    <button
                      type="button"
                      key={tile.v}
                      className={`wiz-tile ${sel ? 'sel' : ''}`}
                      onClick={() => (isMulti ? toggleMulti(tile.v) : chooseSingle(tile))}
                      data-testid={`wizard-tile-${i}`}
                    >
                      <span className="wiz-tile-ico"><Icon size={26} /></span>
                      <span className="wiz-tile-body">
                        <strong>{tile.t}</strong>
                        {tile.d ? <span className="wiz-tile-d">{tile.d}</span> : null}
                      </span>
                      {tile.sugerido ? <span className="wiz-sugerido">{web.wizard.sugerido}</span> : null}
                      {isMulti ? (
                        <span className={`wiz-tick ${sel ? 'on' : ''}`} data-testid={`wizard-tick-${i}`}>
                          {sel ? (screen.key === 'cuidar' ? ord : <IconCheck size={14} />) : null}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
              {isMulti ? (
                <div className="wiz-actions">
                  <button
                    type="button"
                    className="btn-accion"
                    disabled={!multiSel.length}
                    onClick={continueMulti}
                    data-testid="wizard-continuar"
                  >
                    {web.wizard.continuar.replace('(n)', `(${multiSel.length})`)} <IconArrowRight size={16} />
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Celular colombiano: exactamente 10 dígitos y empieza por 3 · se muestra «300 000 0000»
const limpiarNombre = (n) => String(n).replace(/\s+/g, ' ').trim();
const soloDigitos = (t) => String(t).replace(/\D/g, '').slice(0, 10);
const fmtCel = (t) => {
  const d = soloDigitos(t);
  return [d.slice(0, 3), d.slice(3, 6), d.slice(6, 10)].filter(Boolean).join(' ');
};
const validarForm = (form) => {
  const nombre = (limpiarNombre(form.nombre).match(/\p{L}/gu) || []).length >= 2;
  const tel = /^3\d{9}$/.test(soloDigitos(form.tel)) && !form.telLargo;
  return { nombre, tel, ok: nombre && tel && form.consent };
};

function FormView({ web, form, setForm, err, onSubmit }) {
  const v = validarForm(form);
  const telLleno = soloDigitos(form.tel).length;
  const telMal = (telLleno >= 10 || form.telLargo || form.telTocado || err.tel) && telLleno > 0 && !v.tel;
  return (
    <div className="wiz-form" data-testid="wizard-form">
      <label className="wiz-field">
        <span>{web.formulario.nombre}</span>
        <input
          value={form.nombre}
          className={err.nombre ? 'err' : ''}
          autoComplete="given-name"
          onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ') }))}
          data-testid="wizard-nombre"
        />
        {err.nombre ? <em className="wiz-field-err" data-testid="wizard-nombre-error">Escribe tu nombre (mínimo 2 letras).</em> : null}
      </label>
      <label className="wiz-field">
        <span>{web.formulario.whatsapp}</span>
        <input
          value={form.tel}
          className={telMal ? 'err' : ''}
          inputMode="numeric"
          placeholder="300 000 0000"
          onBlur={() => setForm((f) => ({ ...f, telTocado: true }))}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, '');
            setForm((f) => {
              const prev = soloDigitos(f.tel).length;
              if (d.length > 10 && prev === 10 && d.length === 11) return f; // no deja escribir más de 10
              return { ...f, tel: fmtCel(d), telLargo: d.length > 10 };
            });
          }}
          data-testid="wizard-tel"
        />
        {telMal ? <em className="wiz-field-err" data-testid="wizard-tel-error">{web.formulario.error_tel}</em> : null}
      </label>
      <label className={`wiz-consent ${err.consent ? 'err' : ''}`}>
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
          data-testid="wizard-consent"
        />
        <span>{mt(web.formulario.consentimiento)}</span>
      </label>
      <button type="button" className="btn-accion" onClick={onSubmit} disabled={!v.ok} data-testid="wizard-submit">
        {web.formulario.cta}
      </button>
      <p className="wiz-hint">{web.formulario.hint}</p>
    </div>
  );
}

// Buscador de momentos (Fase 9 UX): 4 momentos destacados + búsqueda por texto con sugerencias.
// Sinónimos y normalización viven en engine/momentos.js (compartido con la landing).
const DESTACADOS = ['D1', 'D2', 'D4', 'D5'];

function MomentoPicker({ screen, onPick }) {
  const [q, setQ] = useState('');
  const tileByCode = useCallback(
    (code) => tileByMomentoCode(screen.tiles, code),
    [screen],
  );
  const sugerencias = useMemo(() => {
    const nq = norm(q.trim());
    if (!nq) return [];
    return SINONIMOS.filter((s) => norm(s.t).includes(nq)).slice(0, 6);
  }, [q]);
  return (
    <div data-testid="momento-picker">
      <div className="wiz-tiles" data-testid="wizard-tiles">
        {screen.tiles.map((tile, i) => {
          const Icon = tileIcon('momento', tile.v);
          return (
            <button type="button" key={tile.v} className="wiz-tile" onClick={() => onPick(tile)} data-testid={`wizard-tile-${i}`}>
              <span className="wiz-tile-ico"><Icon size={26} /></span>
              <span className="wiz-tile-body">
                <strong>{tile.t}</strong>
                {tile.d ? <span className="wiz-tile-d">{tile.d}</span> : null}
              </span>
            </button>
          );
        })}
      </div>
      <div className="momento-search">
        <span className="momento-search-k" data-testid="momento-search-label">¿No lo ves? Escríbelo abajo.</span>
        <div className="momento-search-box">
          <IconSearch size={18} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Escribe tu momento… (p. ej. “me mudé”, “nació mi hijo”)"
            data-testid="momento-search-input"
            autoComplete="off"
          />
        </div>
        {sugerencias.length ? (
          <ul className="momento-tips" data-testid="momento-tips">
            {sugerencias.map((s, i) => {
              const tile = tileByCode(s.v);
              const Icon = tileIcon('momento', tile.v);
              return (
                <li key={s.t}>
                  <button type="button" onClick={() => onPick(tile)} data-testid={`momento-tip-${i}`}>
                    <Icon size={18} /> <span>{s.t}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : q.trim() ? (
          <p className="momento-nohit" data-testid="momento-nohit">
            No lo tengo en la lista, pero un asesor te ayuda. Elige “Otra cosa”.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function MapView({ lead, onChat, onAsesor }) {
  const m = web.mapa;
  const conCaso = ['A', 'B', 'C'].includes(lead.go);
  const d1 = String(lead.momento || '').startsWith('D1');
  const texto = d1 ? m.texto_d1 : conCaso ? m.texto_otro : mt(m.texto_sin_caso);
  return (
    <div className="mapa" data-testid="wizard-mapa">
      <StoryRing lead={lead} size={340} />
      <div className="sr-leyenda" data-testid="mapa-leyenda">
        <span><i className="ok" /> Ya lo tienes</span>
        <span><i className="gap" /> Te falta</span>
      </div>
      <p className="mapa-texto" data-testid="mapa-texto">{texto}</p>
      <div className="mapa-salidas">
        {conCaso ? (
          <button type="button" className="btn-accion" onClick={onChat} data-testid="mapa-cta-chat">
            {m.cta_chat}
          </button>
        ) : null}
        <button type="button" className="btn-asesor" onClick={onAsesor} data-testid="mapa-cta-asesor">
          <IconAdvisor size={17} color="#FFFFFF" /> {m.cta_asesor}
        </button>
      </div>
    </div>
  );
}
